import React, { ForwardedRef } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Icon, Tooltip } from '@mui/material';

import { CoreIconProperties } from './CoreIcon.types';

export const CoreIcon = React.forwardRef(
  (
    {
      id,
      dataTest = 'icon',
      icon,
      fixedWidth,
      listItem,
      tabIndex,
      title,
      swapOpacity,
      onClick,
      onMouseEnter,
      onMouseLeave,
      role,
      transform,
      color,
      isDisabled,
      disabledReason,
      style,
      sx,
      className,
    }: CoreIconProperties,
    reference: ForwardedRef<SVGSVGElement>,
  ) => {
    /*
     * AF-22. Клик глушится ЗДЕСЬ, а не `pointer-events: none`, и это решение
     * владельца: показывать недоступность и объяснять её.
     *
     * `pointer-events: none` убирал элемент из целей указателя, и вместе с
     * кликом отключал `cursor` и `title` — пользователь не получал ни
     * курсора, ни объяснения, а видел ровно то же, что на пустом месте рядом.
     *
     * `stopPropagation` обязателен, а не «на всякий случай»: без
     * `pointer-events: none` событие теперь доходит до элемента и пойдёт выше.
     * Родитель у иконки часто кликабелен (строка таблицы, карточка), и
     * выключенная иконка начала бы срабатывать чужим обработчиком. Это
     * единственный НОВЫЙ риск правки; его закрывает тест
     * «не всплывает наверх».
     */
    const handleClick = (event: React.MouseEvent<SVGSVGElement>) => {
      if (isDisabled) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      onClick?.(event);
    };

    /*
     * AF-22. Причина показывается `Tooltip`, а НЕ пропом `title`.
     *
     * Замер (пробник с печатью разметки): `title` не доходит до DOM вообще —
     * ни атрибутом, ни дочерним `<title>`. Теряет его сам `FontAwesomeIcon`,
     * проверено отдельно, без MUI: он ставит `aria-hidden="true"` и подпись
     * не рендерит. То есть проп `title` у этого компонента не работал НИКОГДА,
     * и построить объяснение на нём значило бы повторить ошибку 2025 года —
     * написать правку, которая выглядит сделанной.
     *
     * `Tooltip` — принятый в репозитории механизм (`CardDecisionsView`,
     * `FundTriggerView`), и он работает с выключенным ребёнком ровно потому,
     * что мы больше не снимаем `pointer-events`.
     */
    const iconElement = (
      <Icon
        id={id}
        data-testid={dataTest}
        ref={reference}
        icon={icon}
        fixedWidth={fixedWidth}
        listItem={listItem}
        tabIndex={tabIndex}
        title={isDisabled && disabledReason ? disabledReason : title}
        swapOpacity={swapOpacity}
        onClick={handleClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        role={role}
        transform={transform}
        color={isDisabled ? 'disabled' : color}
        component={FontAwesomeIcon}
        style={style}
        className={className}
        sx={{
          /*
           * AF-22: `pointerEvents` больше НЕ выключается. До этого здесь
           * стояло `isDisabled ? 'none' : 'auto'`, и оно обесценивало строку
           * ниже: элемент снимал с себя приём указателя, поэтому его
           * собственный `cursor` не применялся, а `title` не показывался.
           *
           * Замер AF-19 (в браузере, `elementFromPoint`) показывал: на
           * выключенной иконке пользователь видел курсор ОБЁРТКИ, то есть
           * никакого признака недоступности. Контрольный случай подтверждал
           * механику — у включённой иконки `pointer` доходил.
           *
           * Это уже пытались чинить: коммит `8066fb35` от 07.10.2025 менял
           * `none` на `not-allowed` ИМЕННО ради индикатора и не сработал,
           * потому что `pointer-events: none` стоял рядом. Правку не открыли
           * в браузере, и ложная уверенность прожила десять месяцев.
           *
           * Поэтому проверять эту строку по объявленному значению `cursor`
           * НЕЛЬЗЯ — только вживую.
           *
           * `not-allowed`, а не `none`: второе означает «спрятать курсор»,
           * чего никто не хотел. Теперь, когда `pointerEvents` не выключается,
           * это значение наконец доходит до пользователя — до AF-22 оно было
           * инертным, и выбор между ними ничего не решал.
           */
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          ...sx,
        }}
      />
    );

    if (isDisabled && disabledReason) {
      return <Tooltip title={disabledReason}>{iconElement}</Tooltip>;
    }

    return iconElement;
  },
);

CoreIcon.displayName = 'Icon';
